import { translate } from '#packages/i18n';
import type { EditorAPI } from '#packages/editorAPI';
import * as util from '#packages/util';
import { DATA_BINDING } from '@wix/app-definition-ids';
import _ from 'lodash';
import type {
  CompRef,
  DSRead,
  PlatformAppDescription,
  PageRef,
} from 'types/documentServices';
import * as componentsSelectors from '../components/componentsSelectors';
import { createSelector } from 'reselect';
import { EditorPlatformHostIntegrationAPI } from '@wix/editor-platform-host-integration-apis';
import experiment from 'experiment';
import { editorModel } from '@wix/santa-editor-utils';
import * as DS from '@wix/document-services-types';
import type {
  AddElementsPanelOptions,
  ElementIdentifier,
} from '@wix/platform-editor-sdk';
import type {
  PlatformAppItemDefinition,
  PlatformAppWithCategoryDefinition,
} from '@/platform';
import type {
  NormalizedElement,
  NormalizedItems,
  CheckedState,
  CompRefsMap,
} from '@/compPanels';
import { ComponentType } from '@wix/ambassador-devcenter-v1-component/types';
import type { EditorState } from '#packages/stateManagement';
import { WIX_CODE } from '@wix/app-definition-ids';

const { isRefComponent, isAppWidget } = componentsSelectors;

const { isWidgetController } = util.appStudioUtils;
export interface EssentialData {
  isEssential: boolean;
  text?: string;
}

const isSilentInstallRunning = (state: EditorState): boolean =>
  state.platform.silentInstall.isRunning;

const isUsedSilentInstallOnSite = (state: EditorState): boolean =>
  state.platform.silentInstall.usedOnSite;

const getLatestVersions: (state: EditorState) => Record<string, string> = (
  state,
) => state.platform.latestVersions;
const getAvailableApps = (state: EditorState) => state.platform.availableApps;
const getBlocksAppMap = (state: EditorState) => state.platform.blocksAppMap;
const getFreshAppsData = (state: EditorState) => state.platform.freshAppsData;
const getIsLoading = (state: EditorState) =>
  state.platform.importPanel.isLoading;
const getMasterStructure = (state: EditorState) =>
  state.platform.addElementsPanel.masterStructure;
const getIsApplicationInstallationStatus = (state: EditorState) =>
  state.platform.appInstallationInProcess;
const getIsLoadingAppsError = (state: EditorState) =>
  state.platform.importPanel.isLoadingAppsError;
const getIsPackagesViewRootExpanded = (state: EditorState) =>
  state.platform.packagesView.isRootExpanded;
const getIsImportingApp = (state: EditorState) =>
  state.platform.packagesView.isImportingApp;
const doesHaveAppsToMeasureLoadTime = (state: EditorState) =>
  Object.keys(state.platform.loadBiEvents.loadTimeAppsManifestLoadedIndicator)
    .length > 0;
const haveAllAppManifestsBeenLoaded = (state: EditorState) =>
  Object.values(
    state.platform.loadBiEvents.loadTimeAppsManifestLoadedIndicator,
  ).every(Boolean);
const getLoadedAppManifestsCount = (state: EditorState) =>
  Object.values(
    state.platform.loadBiEvents.loadTimeAppsManifestLoadedIndicator,
  ).filter(Boolean).length;
const hasAllAppManifestsLoadedReported = (state: EditorState) =>
  state.platform.loadBiEvents.hasAllAppManifestsLoadedReported;
const hasEditorScriptsLoadedReported = (state: EditorState) =>
  state.platform.loadBiEvents.hasEditorScriptsLoadedReported;
const hasStartLoadingPlatformBeenReported = (state: EditorState) =>
  state.platform.loadBiEvents.hasStartLoadingPlatformBeenReported;
const hasStartLoadingEditorScriptsBeenReported = (state: EditorState) =>
  state.platform.loadBiEvents.hasStartLoadingEditorScriptsBeenReported;

const isConnectedToDataBindingController = (
  compRef: CompRef,
  dsRead: DSRead,
): boolean =>
  _(compRef)
    .thru(dsRead.platform.controllers.connections.get)
    .map('controllerRef')
    .thru((controllers) =>
      componentsSelectors.getCompsData(controllers as CompRef[], dsRead),
    )
    .some({ applicationId: DATA_BINDING });

export type BindingStyleProperty =
  | 'color'
  | 'iconColor'
  | 'borderColor'
  | 'backgroundColor'
  | 'fillColor'
  | 'strokeColor';
export type BindingStyleState = 'regular' | 'hover' | 'disabled';

const isStylePropertyConnectedToDataBindingController = (
  compRef: CompRef,
  dsRead: DSRead,
  styleProperty: BindingStyleProperty,
  styleState?: BindingStyleState,
): boolean =>
  _(compRef)
    .thru(dsRead.platform.controllers.connections.get)
    .map(({ config, controllerRef }) => ({
      config,
      controller: dsRead.components.data.get(controllerRef as CompRef),
    }))
    .filter({ controller: { applicationId: DATA_BINDING } })
    .flatMap(({ config = {} }) => Object.keys(config.properties ?? {}))
    .includes(
      ['style', styleState, styleProperty]
        .filter((st) => !!st && st !== 'regular')
        .join('.'),
    );

const isDataBindingAppProvisioned = (dsRead: DSRead) => {
  return !!dsRead.platform.getAppDataByAppDefId(WIX_CODE);
};

const getWidgetsDataFromManifest = (
  dsRead: DSRead,
  appDefinitionId: string,
) => {
  const appManifest = dsRead.platform.getAppManifest(appDefinitionId);
  // @ts-expect-error
  return appManifest?.widgets;
};

const getAddPanelCategoryDefinitionFromManifest = (
  dsRead: DSRead,
  appDefinitionId: string,
) => {
  const appManifest = dsRead.platform.getAppManifest(appDefinitionId);
  // @ts-expect-error
  return appManifest?.addPanelSectionDefinition;
};

const isBlocksApp = (app: DS.EditorClientSpecMapEntry): boolean => {
  return Boolean(app.appFields?.platform?.studio);
};

const getBlocksApps = (dsRead: DSRead): DS.EditorClientSpecMapEntry[] => {
  return dsRead.platform.getInstalledApps().filter(isBlocksApp);
};

const filterPlatformAppPlugins = (
  apps: PlatformAppWithCategoryDefinition[],
): PlatformAppWithCategoryDefinition[] => {
  const pluginReferencedCompIds = apps
    .flatMap(({ app }) => (app as DS.AppData).components)
    .reduce((acc, comp) => {
      if (comp.type === ComponentType.WIDGET_PLUGIN) {
        acc.push((comp as DS.WidgetPluginComponent).data.referenceComponentId);
      }

      return acc;
    }, []);

  const pluginReferencedCompIdsSet = new Set(pluginReferencedCompIds);

  const isPluginReferencedDefinition = (
    item: PlatformAppItemDefinition,
  ): boolean => pluginReferencedCompIdsSet.has(item.widgetId);

  return apps.map((app) => ({
    ...app,
    categoryDefinition: {
      ...app.categoryDefinition,
      items: app.categoryDefinition?.items.filter(
        (item) => !isPluginReferencedDefinition(item),
      ),
    },
  }));
};

const getAppAddPanelItems = (
  dsRead: DSRead,
  appDefinitionId: string,
): PlatformAppItemDefinition[] => {
  const app = getBlocksApps(dsRead).find(
    (app) => app.appDefinitionId === appDefinitionId,
  );

  if (!app) {
    return [];
  }

  const appWithCategory: PlatformAppWithCategoryDefinition = {
    app,
    categoryDefinition: getAddPanelCategoryDefinitionFromManifest(
      dsRead,
      appDefinitionId,
    ),
  };

  const platformAppWithCategoryDefinition = filterPlatformAppPlugins([
    appWithCategory,
  ]).find(({ app }) => app.appDefinitionId === appDefinitionId);

  return platformAppWithCategoryDefinition?.categoryDefinition?.items || [];
};

const isBlocksAppHasAddPanelItems = (
  dsRead: DSRead,
  appDefinitionId: string,
): boolean => {
  const addPanelItems = getAppAddPanelItems(dsRead, appDefinitionId);

  return addPanelItems.length > 0;
};

// The "widgets" property of app manifest isn't documented by Editor Platform and so far is used only by Blocks
const hasWidgetsPropertyInManifest = (
  dsRead: DSRead,
  appDefinitionId: string,
): boolean => {
  const widgets = getWidgetsDataFromManifest(dsRead, appDefinitionId);
  return !_.isEmpty(widgets);
};

const isBlocksAppBesidesStudioWidget = (app: PlatformAppDescription) => {
  // @ts-expect-error
  const { components } = app;

  if (!components) return false;

  let hasStudioComponent = false;
  let hasCodeComponent = false;
  let hasDashboardComponent = false;
  for (const component of components) {
    switch (component.type) {
      case ComponentType.STUDIO:
        hasStudioComponent = true;
        break;
      case ComponentType.CODE_PACKAGE:
        hasCodeComponent = true;
        break;
      case ComponentType.BACK_OFFICE_PAGE:
        hasDashboardComponent = true;
        break;
    }
  }

  return hasStudioComponent && (hasCodeComponent || hasDashboardComponent);
};

const getInstalledBlocksApps = (dsRead: DSRead): PlatformAppDescription[] => {
  const installedApps = dsRead.platform.getInstalledEditorApps();

  return !_.isEmpty(installedApps)
    ? installedApps.filter(
        (installedApp) =>
          hasWidgetsPropertyInManifest(dsRead, installedApp.appDefinitionId) ||
          isBlocksAppBesidesStudioWidget(installedApp),
      )
    : [];
};

const getEssentialData = (
  editorAPI: EditorAPI,
  compRef: CompRef,
): EssentialData => {
  const isRefComp = isRefComponent(compRef, editorAPI.dsRead);
  const stageData = isRefComp
    ? editorAPI.dsRead.appStudioWidgets.getStageData(compRef)
    : editorAPI.dsRead.platform.controllers.getConnectedComponentStageData(
        compRef,
      );

  const compName = editorAPI.components.getDisplayName(compRef);
  const controllerRef =
    editorAPI.dsRead.platform.controllers.connections.getPrimaryConnection(
      compRef,
    )?.controllerRef;

  return getEssentialDataFromManifest(
    editorAPI,
    stageData,
    compName,
    controllerRef as CompRef,
  );
};

const getEssentialDataByRole = (
  editorAPI: EditorAPI,
  controllerRef: CompRef,
  role: string,
): EssentialData => {
  const stageData = editorAPI.dsRead.platform.controllers.getStageDataByRole(
    controllerRef as CompRef,
    role,
  );
  return getEssentialDataFromManifest(
    editorAPI,
    stageData,
    role,
    controllerRef as CompRef,
  );
};

const getEssentialDataFromManifest = (
  editorAPI: EditorAPI,
  stageData: AnyFixMe,
  compName: string,
  controllerRef: CompRef,
): EssentialData => {
  const essentialData = stageData?.behavior?.essential;
  if (!essentialData?.enabled) {
    return {
      isEssential: false,
    };
  }

  const { text } = essentialData;

  return {
    isEssential: true,
    text: text || getDefaultEssentialText(editorAPI, compName, controllerRef),
  };
};

const getDefaultEssentialText = (
  editorAPI: EditorAPI,
  compName: string,
  controllerRef: CompRef,
): string => {
  const widgetName =
    controllerRef && editorAPI.components.getDisplayName(controllerRef);
  return translate('AppStudio_Essential_Elements_Missing_Shared_Tooltip', {
    Element_Name: compName,
    Widget_Name: widgetName,
  });
};

const isRefComponentSupported = (editorAPI: EditorAPI): boolean => {
  // `santa` is currently used in the old unit tests testkit
  return editorAPI.dsRead.env?.viewer.getViewerName() !== 'santa';
};

const shouldWidgetHaveAddElements = (
  editorAPI: EditorAPI,
  compRef: CompRef,
) => {
  if (!isAppWidget(compRef, editorAPI.dsRead)) {
    return false;
  }

  const [widgetRootRef] = editorAPI.components.getChildren(compRef);
  const isContainer =
    widgetRootRef && editorAPI.dsRead.components.is.container(widgetRootRef);
  if (!isContainer) {
    return false;
  }

  if (isWidgetController(editorAPI, compRef)) {
    return true;
  }

  if (!util.appStudioUtils.isAppStudio()) {
    return false;
  }

  const parentRef = editorAPI.components.getContainer(compRef);
  const isInsidePage =
    editorAPI.components.getType(parentRef) === 'mobile.core.components.Page';
  return !isInsidePage;
};

const packagesView = {
  getIsImportingApp,
  getIsRootExpanded: getIsPackagesViewRootExpanded,
};

const getInstalledAppsDependency = (dsRead: DSRead, appDefinitionId: string) =>
  dsRead.platform
    .getInstalledApps()
    .reduce((installedAppsDependencyInfo, appData) => {
      const appDependencyInfo = dsRead.platform
        .getAppsDependenciesWithPredicate(
          [appData.appDefinitionId],
          (dependency) =>
            dsRead.platform.isAppActive(dependency.appDefinitionId),
        )
        .find((app) => app.appDefinitionId === appDefinitionId);
      if (appDependencyInfo) {
        installedAppsDependencyInfo.push({
          appDefinitionId: appData.appDefinitionId,
          isRequired: appDependencyInfo.isRequired || false,
        });
      }
      return installedAppsDependencyInfo;
    }, []);
const selectSections = (_: unknown, props: AddElementsPanelOptions) =>
  props.sectionsData;
const selectCategories = (_: unknown, props: AddElementsPanelOptions) =>
  props.categoriesData;
const selectItems = (_: unknown, props: AddElementsPanelOptions) => ({
  elementsData: props.elementsData as NormalizedElement[],
  widgetRef: props.widgetRef,
});

const sortItemsHandler = (
  { index: indexOne }: AnyFixMe,
  { index: indexTwo }: AnyFixMe,
) => indexOne - indexTwo;
const sortItems = (normalizedItems: NormalizedItems) => {
  // sort items with section
  Object.keys(normalizedItems.sectionsWithItems).forEach((sectionKey) => {
    normalizedItems.sectionsWithItems[sectionKey].unCategorizedItems.sort(
      sortItemsHandler,
    );
    Object.keys(
      normalizedItems.sectionsWithItems[sectionKey].categoriesWithItems,
    ).forEach((categoryKey) => {
      normalizedItems.sectionsWithItems[sectionKey].categoriesWithItems[
        categoryKey
      ].children.sort(sortItemsHandler);
    });
  });
  // sort items without section
  normalizedItems.notInSectionItems.unCategorizedItems.sort(sortItemsHandler);
  Object.keys(normalizedItems.notInSectionItems.categoriesWithItems).forEach(
    (categoryKey) => {
      normalizedItems.notInSectionItems.categoriesWithItems[
        categoryKey
      ].children.sort(sortItemsHandler);
    },
  );
  return normalizedItems;
};

const setCompRefAndCheckedStatus = (
  item: NormalizedElement,
  getIdentifierFromRoleAndSubRole: (
    elementIdentifier: ElementIdentifier,
  ) => string,
  data: {
    checkedState: CheckedState;
    compRefsMap: CompRefsMap;
  },
) => {
  const id = getIdentifierFromRoleAndSubRole(item.identifier);
  if (item.compRef) {
    data.checkedState[id] = !item.isCollapsed;
    data.compRefsMap[id] = item.compRef;
  }

  return data;
};
const getSelectItemsByCategory = (
  editorAPI: EditorAPI,
  findConnectionByRole: (
    elementIdentifier: ElementIdentifier,
    connectedComponents: ElementIdentifier[],
  ) => ElementIdentifier,
  getIdentifierFromRoleAndSubRole: (
    elementIdentifier: ElementIdentifier,
  ) => string,
) =>
  createSelector(
    [selectSections, selectCategories, selectItems],
    (sections, categories, elements) => {
      let data = {
        checkedState: {},
        compRefsMap: {},
      };
      const primaryConnectionOfConnected =
        editorAPI.dsRead.platform.controllers.connections
          .getConnectedComponents(elements.widgetRef, true)
          .map((compRef) => {
            const primaryConnection =
              editorAPI.dsRead.platform.controllers.connections.getPrimaryConnection(
                compRef,
              );
            return { ...primaryConnection, compRef };
          });
      const normalizedItems = elements.elementsData.reduce<NormalizedItems>(
        (acc, item) => {
          const fullItem = { ...item };
          // @ts-expect-error
          let { compRef } =
            findConnectionByRole(
              item.identifier,
              primaryConnectionOfConnected,
            ) || {};
          if (compRef) {
            if (isAppWidget(compRef, editorAPI.dsRead)) {
              compRef =
                editorAPI.components.getContainer_DEPRECATED_BAD_PERFORMANCE(
                  compRef,
                );
            }
            const properties =
              editorAPI.dsRead.components.properties.get(compRef);
            fullItem.isCollapsed = properties?.ghost === 'COLLAPSED';
            const compBehavior =
              editorAPI.dsRead.platform.controllers.getConnectedComponentStageData(
                compRef,
                // @ts-expect-error
              )?.behavior;
            fullItem.isNonRemovable = compBehavior?.preventHide;
            fullItem.compRef = compRef;
          }
          data = setCompRefAndCheckedStatus(
            fullItem,
            getIdentifierFromRoleAndSubRole,
            data,
          );

          fullItem.displayName =
            item.label ||
            (compRef && editorAPI.components.getDisplayName(compRef));
          /// items with section
          if (item.sectionId) {
            if (!acc.sectionsWithItems[item.sectionId]) {
              const sectionIndex = sections?.findIndex(
                ({ id }) => id === item.sectionId,
              );
              acc.sectionsWithItems[item.sectionId] = {
                ...sections[sectionIndex],
                unCategorizedItems: [],
                categoriesWithItems: {},
              };
            }
            // an item with section & category
            if (item.categoryId) {
              if (
                !acc.sectionsWithItems[item.sectionId].categoriesWithItems[
                  item.categoryId
                ]
              ) {
                const categoryIndex = categories.findIndex(
                  ({ id }) => id === item.categoryId,
                );
                acc.sectionsWithItems[item.sectionId].categoriesWithItems[
                  item.categoryId
                ] = {
                  ...categories[categoryIndex],
                  categoryIndex,
                  children: [],
                };
              }
              acc.sectionsWithItems[item.sectionId].categoriesWithItems[
                item.categoryId
              ].children.push(fullItem);
              // an item with section (without category)
            } else {
              acc.sectionsWithItems[item.sectionId].unCategorizedItems.push(
                fullItem,
              );
            }
            /// items without section
            // an item with category (without section)
          } else if (item.categoryId) {
            if (!acc.notInSectionItems.categoriesWithItems[item.categoryId]) {
              const categoryIndex = categories.findIndex(
                ({ id }) => id === item.categoryId,
              );
              acc.notInSectionItems.categoriesWithItems[item.categoryId] = {
                ...categories[categoryIndex],
                categoryIndex,
                children: [],
              };
            }
            acc.notInSectionItems.categoriesWithItems[
              item.categoryId
            ].children.push(fullItem);
            // an item without section & without category
          } else {
            acc.notInSectionItems.unCategorizedItems.push(fullItem);
          }
          return acc;
        },
        {
          sectionsWithItems: {},
          notInSectionItems: {
            unCategorizedItems: [],
            categoriesWithItems: {},
          },
        },
      );
      return { items: sortItems(normalizedItems), data };
    },
  );

const isAppWithReplacerPage = (
  editorAPI: EditorAPI,
  appDefinitionId: string,
) => {
  const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
  const appPagesData = editorAPI.dsRead.pages
    .getPagesData()
    .filter((data) => data?.managingAppDefId === appDefinitionId);
  return appPagesData.some((pageData) =>
    platformAPI.pageReplace.isReplacer(
      editorAPI.dsRead.pages.getReference(pageData.id) as PageRef,
    ),
  );
};

// since this day we decided to install pending page apps silently
// we do not want to install pending for old sites
const startingPoint = new Date('05/01/23').getTime();
const shouldInstallPendingSilently = (editorAPI: EditorAPI) => {
  const isNewSite = startingPoint < editorModel.siteHeader.dateCreated;

  const usedSilentInstall = isUsedSilentInstallOnSite(
    editorAPI.store.getState(),
  );

  return (
    util.sections.isSectionsEnabled() &&
    experiment.isOpen('se_installPendingAppsOnSilentInstall') &&
    (usedSilentInstall || isNewSite)
  );
};

const isAppEditableInBlocks = (appDefId: string, state: EditorState) => {
  return !!getBlocksAppMap(state)[appDefId];
};

export {
  isConnectedToDataBindingController,
  isStylePropertyConnectedToDataBindingController,
  isDataBindingAppProvisioned,
  getIsLoading,
  getIsLoadingAppsError,
  getAvailableApps,
  getFreshAppsData,
  getWidgetsDataFromManifest,
  getAddPanelCategoryDefinitionFromManifest,
  getInstalledBlocksApps,
  shouldInstallPendingSilently,
  getIsApplicationInstallationStatus,
  doesHaveAppsToMeasureLoadTime,
  haveAllAppManifestsBeenLoaded,
  getLoadedAppManifestsCount,
  hasAllAppManifestsLoadedReported,
  hasEditorScriptsLoadedReported,
  hasStartLoadingPlatformBeenReported,
  hasStartLoadingEditorScriptsBeenReported,
  isBlocksAppHasAddPanelItems,
  getMasterStructure,
  getEssentialData,
  getEssentialDataByRole,
  shouldWidgetHaveAddElements,
  isRefComponentSupported,
  getInstalledAppsDependency,
  packagesView,
  getSelectItemsByCategory,
  isSilentInstallRunning,
  getLatestVersions,
  isAppWithReplacerPage,
  isAppEditableInBlocks,
  filterPlatformAppPlugins,
};
